/**
 * The last tool page the reader looked at, remembered for the chat page.
 *
 * ROADMAP "AI assistant — page context", part (a). This lives in a store
 * rather than in `FloatingChatProvider`'s own state because the provider is
 * remounted on the way to `/chat` (verified headlessly 2026-09-17: a fresh
 * mount id on every navigation into the chat route), and state held there
 * was gone by the time the chat page asked for it. A store survives any
 * remount, and it is kept in sessionStorage so a full page load in the same
 * tab keeps it too; a new tab starts clean.
 */
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { PageContext } from '../contexts/FloatingChatContext';

interface PageContextMemoryState {
  lastToolContext: PageContext | null;
  remember: (context: PageContext) => void;
  forget: () => void;
}

export const usePageContextMemory = create<PageContextMemoryState>()(
  persist(
    (set) => ({
      lastToolContext: null,
      remember: (context) => set({ lastToolContext: context }),
      forget: () => set({ lastToolContext: null }),
    }),
    {
      // The tab, not the browser: a reader who types /chat into the same tab
      // after looking at a stock still gets the chip; a new tab starts clean.
      name: 'indexalpha-last-tool-context',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({ lastToolContext: state.lastToolContext }),
    },
  ),
);
